type VonageRegion = 'US' | 'EU' | 'AP';

interface IpWhoResponse {
  success: boolean;
  latitude: number;
  longitude: number;
  country_code: string;
}

const REGION_CENTERS = {
  US: { lat: 39.8283, lon: -98.5795 },
  EU: { lat: 50.1109, lon: 8.6821 },
  AP: { lat: 1.3521, lon: 103.8198 },
} as const;

const EU_COUNTRIES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'NO',
  'CH',
  'IS',
  'UA',
  'TR',
  'RS',
  'BA',
  'ME',
  'MK',
  'AL',
]);

const US_COUNTRIES = new Set(['US', 'CA', 'MX']);

const AP_COUNTRIES = new Set([
  'JP',
  'KR',
  'CN',
  'TW',
  'HK',
  'MO',
  'MN',
  'SG',
  'MY',
  'TH',
  'ID',
  'PH',
  'VN',
  'KH',
  'LA',
  'MM',
  'BN',
  'TL',
  'IN',
  'PK',
  'BD',
  'LK',
  'NP',
  'BT',
  'MV',
  'AU',
  'NZ',
  'FJ',
  'PG',
]);

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Returns the country code based on the device's IP geolocation.
 * Uses multiple services with fallbacks for reliability.
 */
export async function getCountryCode(): Promise<string | null> {
  // Try Cloudflare first
  try {
    const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
      signal: AbortSignal.timeout(3000),
    });

    if (response.ok) {
      const text = await response.text();
      const locMatch = text.match(/loc=([A-Z]{2})/);
      const countryCode = locMatch?.[1];
      if (countryCode) {
        return countryCode;
      }
    }
  } catch {
    // Continue to next service
  }

  // Try ipwho.is as last resort
  try {
    const response = await fetch('https://ipwho.is/', {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(3000),
    });

    if (response.ok) {
      const data = (await response.json()) as IpWhoResponse;
      if (data.success && data.country_code) {
        return data.country_code;
      }
    }
  } catch (error) {
    console.warn('[getCountryCode] All geolocation services failed:', error);
  }

  return null;
}

/**
 * Try Cloudflare's geolocation service (fast, reliable, no auth needed)
 */
async function tryCloudflare(): Promise<VonageRegion | null> {
  try {
    const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    const locMatch = text.match(/loc=([A-Z]{2})/);
    const countryCode = locMatch?.[1];

    if (!countryCode) {
      return null;
    }

    if (EU_COUNTRIES.has(countryCode)) {
      return 'EU';
    }
    if (US_COUNTRIES.has(countryCode)) {
      return 'US';
    }
    if (AP_COUNTRIES.has(countryCode)) {
      return 'AP';
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Try ipwho.is (keeping as fallback)
 */
async function tryIpWho(): Promise<VonageRegion | null> {
  try {
    const response = await fetch('https://ipwho.is/', {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as IpWhoResponse;

    if (!data.success) {
      return null;
    }

    const { country_code, latitude, longitude } = data;

    if (EU_COUNTRIES.has(country_code)) {
      return 'EU';
    }
    if (US_COUNTRIES.has(country_code)) {
      return 'US';
    }
    if (AP_COUNTRIES.has(country_code)) {
      return 'AP';
    }

    const distances: Array<[VonageRegion, number]> = [
      ['US', haversineDistance(latitude, longitude, REGION_CENTERS.US.lat, REGION_CENTERS.US.lon)],
      ['EU', haversineDistance(latitude, longitude, REGION_CENTERS.EU.lat, REGION_CENTERS.EU.lon)],
      ['AP', haversineDistance(latitude, longitude, REGION_CENTERS.AP.lat, REGION_CENTERS.AP.lon)],
    ];

    return distances.reduce((closest, current) => (current[1] < closest[1] ? current : closest))[0];
  } catch {
    return null;
  }
}

/**
 * Returns "US", "EU", or "AP" based on the device's IP geolocation.
 * Uses multiple fallback services for reliability:
 * 1. Cloudflare (fast, reliable)
 * 2. ipwho.is (fallback)
 * 3. Fallback to 'US'
 */
export async function getVonageRegion(fallback: VonageRegion = 'US'): Promise<VonageRegion> {
  // Try Cloudflare first (most reliable)
  const cloudflareResult = await tryCloudflare();
  if (cloudflareResult) {
    console.info('[getVonageRegion] Detected region via Cloudflare:', cloudflareResult);
    return cloudflareResult;
  }

  console.warn('[getVonageRegion] Cloudflare failed, trying ipwho.is');

  // Try ipwho.is as second option
  const ipWhoResult = await tryIpWho();
  if (ipWhoResult) {
    console.info('[getVonageRegion] Detected region via ipwho.is:', ipWhoResult);
    return ipWhoResult;
  }

  // All services failed, use fallback
  console.warn('[getVonageRegion] All geolocation services failed, using fallback:', fallback);
  return fallback;
}
